import React, { useCallback, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { BodyText, Icon, theme } from '@sg/garnish';

import { BagFeaturedUpsellButton } from '../BagFeaturedUpsellButton';

// ─────────────────────────────────────────────────────────────────────────────

export const BagFeaturedUpsellAddButton = (props: Props) => {
  const { status, label, onPress } = props;

  const { formatMessage } = useIntl();

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const reelPosition = useSharedValue(REEL_POSITIONS[status]);

  // ─── Derived ───────────────────────────────────────────────────────────────

  const accessibilityLabels: Record<Status, string> = {
    idle: label,
    adding: formatMessage(messages.adding),
    added: formatMessage(messages.added),
  };

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const animateToStatus = useCallback(() => {
    reelPosition.value = withTiming(REEL_POSITIONS[status], REEL_TIMING_CONFIG);
  }, [reelPosition, status]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const reelAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: `${-reelPosition.value * 100}%` as const }],
  }));

  const reelStyles = [styles.reel, reelAnimatedStyle];
  const addingRowStyles = [styles.row, styles.addingRow];
  const addedRowStyles = [styles.row, styles.addedRow];

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(animateToStatus, [animateToStatus]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <BagFeaturedUpsellButton
      palette="primary"
      label={accessibilityLabels[status]}
      disabled={status !== 'idle'}
      onPress={onPress}
    >
      <Animated.View style={reelStyles}>
        <View style={styles.row}>
          <Icon name="IconAddToBag" width={24} height={24} color={theme.colors.DARK_KALE} />

          <BodyText sizeMatch={['18']} style={styles.label}>
            {label}
          </BodyText>
        </View>

        <View style={addingRowStyles}>
          <BodyText sizeMatch={['18']} style={styles.label}>
            <FormattedMessage {...messages.adding} />
          </BodyText>
        </View>

        <View style={addedRowStyles}>
          <Icon name="IconCheck2" width={28} height={28} color={theme.colors.DARK_KALE} />
        </View>
      </Animated.View>
    </BagFeaturedUpsellButton>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Rows of the label reel, top to bottom. The reel slides up as the add moves
 * forward.
 */
const REEL_POSITIONS: Record<Status, number> = { idle: 0, adding: 1, added: 2 };

const REEL_TIMING_CONFIG = { duration: 600, easing: Easing.out(Easing.cubic) };

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  adding: {
    defaultMessage: 'Adding…',
    description: 'Bag | Featured upsell | Add CTA while adding',
  },
  added: {
    defaultMessage: 'Added to bag',
    description: 'Bag | Featured upsell | Add CTA a11y label once added',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  reel: {
    ...StyleSheet.absoluteFill,
  },
  row: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['1'],
    height: '100%',
  },
  addingRow: {
    top: '100%',
  },
  addedRow: {
    top: '200%',
  },
  label: {
    color: theme.colors.DARK_KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  status: Status;
  label: string;
  onPress: () => void;
};

type Status = 'idle' | 'adding' | 'added';
